import Icon from "@/components/Icon/Icon";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/cn";
import { defaultNS, useTranslation } from "@/lib/i18n";
import type { Column } from "@tanstack/react-table";

const DISPLAY_LABEL_REGEX = /^(.+?)\[.+\]$/;

export interface DataTableColumnHeaderProps<TData, TValue>
  extends React.HTMLAttributes<HTMLDivElement> {
  column: Column<TData, TValue>;
  align?: "left" | "right";
}

export function DataTableColumnHeader<TData, TValue>({
  column,
  align = "left",
  className,
}: DataTableColumnHeaderProps<TData, TValue>) {
  const { t } = useTranslation(defaultNS, {
    keyPrefix: "component.dataTable.columnAction",
  });

  const { t: tHeaders } = useTranslation(defaultNS, {
    keyPrefix: "component.dataTable.headers",
  });

  /**
   * 1. 表示ラベル: API から翻訳済みラベルが返される場合はそれを使用
   * 2. i18nKey: 表示ラベルがない場合は従来の翻訳キーを使用
   * 3. デフォルト値: どちらもない場合は "-" を表示
   */
  const displayText = (() => {
    const meta = column.columnDef.meta;

    if (meta?.displayLabel) {
      return extractDisplayText(meta.displayLabel);
    }

    if (meta?.i18nKey) {
      return tHeaders(meta.i18nKey);
    }

    return "-";
  })();

  return (
    <DropdownMenu>
      <div
        className={cn(
          "flex items-center",
          align === "left" && "justify-start",
          align === "right" && "justify-end",
          className,
        )}
      >
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className={cn(
              "h-8 font-semibold text-neutral-muted-on-fill data-[state=open]:bg-primary-muted-fill",
              align === "left" && "-ml-3",
              align === "right" && "-mr-3",
            )}
          >
            {column.getCanSort() ? (
              <>
                {column.getIsSorted() === "desc" ? (
                  <Icon
                    iconName="ChevronDownIcon"
                    text={displayText}
                    variant="xs"
                    right
                  />
                ) : column.getIsSorted() === "asc" ? (
                  <Icon
                    iconName="ChevronUpIcon"
                    text={displayText}
                    variant="xs"
                    right
                  />
                ) : (
                  <Icon
                    iconName="ChevronsUpDownIcon"
                    text={displayText}
                    variant="xs"
                    right
                  />
                )}
              </>
            ) : (
              <span>{displayText}</span>
            )}
          </Button>
        </DropdownMenuTrigger>
      </div>
      <DropdownMenuContent align="start">
        {column.getCanSort() && (
          <>
            <DropdownMenuItem onClick={() => column.toggleSorting(false)}>
              <Icon iconName="ChevronUpIcon" text={t("sortAsc")} variant="xs" />
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => column.toggleSorting(true)}>
              <Icon
                iconName="ChevronDownIcon"
                text={t("sortDesc")}
                variant="xs"
              />
            </DropdownMenuItem>
            <DropdownMenuSeparator />
          </>
        )}
        <DropdownMenuItem onClick={() => column.toggleVisibility(false)}>
          <Icon iconName="EyeOff" text={t("hide")} variant="xs" />
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/**
 * 表示ラベルから表示テキストを抽出
 *
 * @param displayLabel "従業員番号[employeeNumber]" または undefined
 * @returns "従業員番号" または "-"
 */
function extractDisplayText(displayLabel?: string): string {
  if (!displayLabel || displayLabel.trim() === "") {
    return "-";
  }

  const match = displayLabel.match(DISPLAY_LABEL_REGEX);
  return match && match[1] ? match[1].trim() : displayLabel;
}
